<script setup lang="ts">
import draggable from 'vuedraggable'
import type MDBSemi from '@/types/ferme/semi'
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { notifyError, roundTo, to_wd, to_dd_MM, to_wd_dd_MMMM_hh_mm, emptyMongoId, computeSelectionSum, getSemiVérifiéColor, getSemiÉchantillonColor } from '@/utils/helpers'
import { unitésParBoîte, getBucketBigType } from '@/utils/helpers-vegco'

definePage({
  meta: {
    id: 'vgco.rapportsemaine',
    title: 'Rapport semaine',
    icon: 'mdi-sprout',
    drawerIndex: 4,
    requiresAuth: true,
    requiresSiteAccess: 'vegco'
  },
})

const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const isLoading = ref(false)
const newData = ref(false)

const weekSelector = useTemplateRef('weekSelector')
const selectedSite = ref(storeU.siteSélectionné.lettresDappel)
const selectedWeek = ref(new Date(storeU.journéeSélectionnée))
const numberOfDays = ref(14)
const numberOfWeeks = ref(2)
const buckets = ref([] as MDBVegcoBucket[])
const selectedSum = ref<number | null>(null)
const commandeBuckets = ref([] as MDBVegcoBucket[])
const days = ref([] as (Date | undefined)[])
const cultures = ref([] as string[])
const semis = [] as MDBSemi[]
const semisVegco = [] as MDBSemi[]
const tableRows = [
  { label: 'Modifier', field: 'buckets' },
  { label: 'Producteur', field: 'producteurCalc' },
  { label: 'Champ', field: 'champCalc' },
  { label: 'Semi', field: 'numéroSemi' },
  { label: 'Variété', field: 'variété' },
  { label: 'Acres', field: 'superficieCalc' },
  { label: 'Balises', field: 'balisesCalc' },
  { label: 'Total prévision', field: 'prévisionCalc' },
  { label: 'Boîtes restantes', field: 'quantitéCalc' },
  { label: 'Échantillon', field: 'échantillon' },
  { label: 'Commentaires', field: 'commentairesCalc' },
  { label: 'Total journée', field: 'totalQté', thClass: 'small-borders-bottom' },
]
const sites = storeU.utilisateur.pagesListeNoire.includes('vgco.tous') ?
  [storeU.siteSélectionné.lettresDappel] : ['Tous', 'DLFL', 'FHVW', 'PHVW', 'JPLG']

watch(selectedWeek, async (_) => {
  await nextTick()
  getData()
})

async function onWeekNumberChanged() {
  numberOfDays.value = numberOfWeeks.value * 7
  await nextTick()
  getData()
}

async function getData() {
  try{
    if (!weekSelector.value) {
      notifyError('Erreur: le sélecteur de semaine est introuvable.')
      return
    }

    isLoading.value = true
    const weekStart = weekSelector.value.getWeekStart()
    const weekEnd = weekSelector.value.getWeekEnd()

    const date = new Date(weekStart)
    days.value = []
    for (let i = 0; i < numberOfDays.value; i++) {
      if (i > 0 && i % 7 == 0) {
        days.value.push(undefined)
      }
      days.value.push(new Date(date.getTime()))
      date.setDate(date.getDate() + 1)
    }

    cultures.value = []
    for (const culture of ['Iceberg / Pommée', 'Laitue Romaine',
      'Laitue Feuille Verte', 'Laitue Feuille Rouge', 'Bok Choy', 'Nappa']) {
      if (storeU.cultures.find(c => c.nom == culture)) {
        cultures.value.push(culture)
      }
    }

    if (selectedSite.value == 'VGCO') {
      selectedSite.value = 'Tous'
    }

    await storeVegco.loadDaysMultipleCultures(weekStart, weekEnd, cultures.value)
    const siteCS = selectedSite.value == 'Tous' ? 'VGCO' : selectedSite.value
    const siteId = await storeU.getOrFetchSiteId(siteCS)

    const bucketsList = storeVegco.bucketsStore.filter(bucket => {
      const date = new Date(bucket.date)
      return date >= weekStart && date < weekEnd &&
        (bucket.siteId == siteId || bucket.siteGroupeId == siteId)
    })

    // Load new semis
    {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const newSemiIds = []
      for (const bucket of bucketsList) {
        if (bucket.siteId == storeU.siteSélectionné.id &&
          bucket.semiId != emptyMongoId() && !semis.find(semi => semi.id == bucket.semiId)) {
          newSemiIds.push(bucket.semiId)
        }
      }

      if (newSemiIds.length != 0) {
        const result = await apiProphet.post(`sites/${siteCS}/semis/multiple`, newSemiIds)
        semis.push(... result.data as MDBSemi[])
      }

      newSemiIds.length = 0
      for (const bucket of bucketsList) {
        if (bucket.siteId != storeU.siteSélectionné.id &&
          bucket.semiId != emptyMongoId() && !semisVegco.find(semi => semi.id == bucket.semiId)) {
          newSemiIds.push(bucket.semiId)
        }
      }

      if (newSemiIds.length != 0) {
        const result = await apiProphet.post(`vgco/semis/multiple`, newSemiIds)
        semisVegco.push(... result.data as MDBSemi[])
      }
    }

    // Filter commande buckets
    commandeBuckets.value = bucketsList.filter(bucket => bucket.commandeVenteId != emptyMongoId())

    // Only master buckets
    buckets.value = bucketsList
      .filter(bucket => bucket.semiId != emptyMongoId() && bucket.commandeVenteId == emptyMongoId())
      .map(bucket => ({
        ...bucket,
        prévisionCalc: getBucketQuantitéTotalFormatted(bucket),
        quantitéCalc: getBucketQuantitéFormatted(bucket),
        producteurCalc: storeVegco.getSiteLettresDappelRenommé(bucket.siteId),
        balisesCalc: getBucketBigType(bucket),
        champCalc: getBucketChamp(bucket),
        superficieCalc: getBucketRealSuperficie(bucket),
        commentairesCalc: bucket.commentaires && bucket.commentaires.length > 20
          ? bucket.commentaires.slice(0, 20) + '...'
          : bucket.commentaires
      }))

    newData.value = false
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

function getBucketSemi(bucket: MDBVegcoBucket) {
  if (bucket.semiId == emptyMongoId()) {
    return undefined
  }
  const semi = semis.find(semi => semi.id == bucket.semiId)
  if (semi != undefined) {
    return semi
  }
  return semisVegco.find(semi => semi.id == bucket.semiId)
}

function getBucketChamp(bucket: MDBVegcoBucket) {
  const semi = getBucketSemi(bucket)
  if (semi == undefined) {
    return '?'
  }
  return semi.champ
}

function getBuckets(culture: string, day: Date | undefined) {
  if (day == undefined) {
    return []
  }
  return buckets.value.filter(bucket => {
    const date = new Date(bucket.date)
    return date.getFullYear() == day.getFullYear() && date.getMonth() == day.getMonth() &&
      date.getDate() == day.getDate() && bucket.culture == culture
  })
}

function getBucketQuantitéTotalFormatted(bucket: MDBVegcoBucket) {
  let qté = bucket.quantité
  if (bucket.semiFermé)
   qté = 0

   commandeBuckets.value.forEach(cmdBucket => {
    if (cmdBucket.bucketParentId == bucket.id) {
      qté += cmdBucket.quantité
    }
  })

  return roundTo(qté / unitésParBoîte(bucket.culture), 0)
}

function getBucketQuantitéFormatted(bucket: MDBVegcoBucket) {
  let qté = bucket.quantité
  if (bucket.semiFermé)
   qté = 0

  return roundTo((qté / unitésParBoîte(bucket.culture)), 0)
}

function getBucketCommandesTooltip(bucket: MDBVegcoBucket): string {
  const related = commandeBuckets.value.filter(cb => cb.bucketParentId === bucket.id)
  if (related.length === 0) return ''

  const byDate = new Map<string, MDBVegcoBucket[]>()
  for (const cb of related) {
    const dateKey = to_dd_MM(new Date(cb.date))
    if (!byDate.has(dateKey)) byDate.set(dateKey, [])
    byDate.get(dateKey)!.push(cb)
  }

  let tooltip = 'Commandes attribuées:\n\n'
  for (const [dateKey, cbs] of byDate) {
    tooltip += `${dateKey}:\n`
    for (const cb of cbs) {
      const commande = storeVegco.commandesStore.find(c => c.id === cb.commandeVenteId)
      const qté = roundTo(cb.quantité / unitésParBoîte(cb.culture), 0)
      const cmdNum = commande ? `#${commande.noCommande}` : '?'
      tooltip += `  ${cmdNum}: ${qté} boîtes`
      if (cb.codeProduit) tooltip += ` (${cb.codeProduit})`
      tooltip += '\n'
    }
  }
  return tooltip.trim()
}

function getBucketRealQuantité(bucket: MDBVegcoBucket) {
  let qté = bucket.quantité
  commandeBuckets.value.reduce((acc, cmdBucket) => {
    if (cmdBucket.bucketParentId == bucket.id) {
      qté += cmdBucket.quantité
    }
    return acc
  }, 0)
  return qté
}

function getBucketRealSuperficie(bucket: MDBVegcoBucket) {
  const semi = getBucketSemi(bucket)
  if (semi == undefined) {
    return '?'
  }

  let prorata = 1
  const otherMasterBucket = buckets.value.find(b => b.semiId == semi.id && b.id != bucket.id)
  if (otherMasterBucket != undefined) {
    const myRealQuantité = getBucketRealQuantité(bucket)
    const otherRealQuantité = getBucketRealQuantité(otherMasterBucket)
    prorata = myRealQuantité / (myRealQuantité + otherRealQuantité)
  }
  return roundTo(semi.superficie * prorata, 2)
}

function getTotalForDay(culture: string, day: Date | undefined) {
  let total = 0
  const bucketsForDay = getBuckets(culture, day)
  for (const bucket of bucketsForDay) {
    if (!bucket.semiFermé) {
      total += bucket.quantité / unitésParBoîte(bucket.culture)
    }
  }
  const commandeB = commandeBuckets.value.filter(cmdBucket => {
    const date = new Date(cmdBucket.date)
    return date.getFullYear() == day?.getFullYear() && date.getMonth() == day?.getMonth() &&
      date.getDate() == day?.getDate() && cmdBucket.culture == culture
  })
  for (const bucket of commandeB) {
    total += bucket.quantité / unitésParBoîte(bucket.culture)
  }
  return total != 0 ? roundTo(total, 0) : ''
}

function getColumnSize(idx: number) {
  if (display.xs.value) {
    if (idx % 8 == 7) return '25px'
    else return '100px'
  }

  if (idx % 8 == 7) return '75px'
  else return '150px'
}

const theme = useTheme()
const display = useDisplay()

function getColumnColor(idx: number) {
  const themeName = theme.global.name.value
  if (idx % 8 == 7) {
    return ''
  }
  if ((idx / 8) % 2 < 1) {
    return themeName == 'dark' ? '#104010' : '#A0FFA0'
  }
  else {
    return themeName == 'dark' ? '#505010' : '#FFFFA0'
  }
}

function getCellClass(row: any, day: any) {
  if (day == undefined && row?.field == 'totalQté') {
    return 'gap-between-weeks small-borders-bottom'
  } else if (row && row.thClass) {
    return row.thClass
  } else if (day == undefined) {
    return 'gap-between-weeks'
  }
  return 'small-borders'
}

const dialogBucket = useTemplateRef('dialogBucket')
const dialogBuckets = useTemplateRef('dialogBuckets')
async function openBuckets(bucket: MDBVegcoBucket, day: Date) {
  if (bucket.siteId != storeU.siteSélectionné.id) {
    const modifs = await dialogBucket.value?.openBucketEdit(bucket, day)
    if (modifs) {
      getData()
    }
  } else {
    const semi = semis.find(semi => semi.id == bucket.semiId)
    if (semi == undefined) {
      notifyError('Erreur: le semi n\'existe pas.')
      return
    }
    const modifs = await dialogBuckets.value?.openBucketsAsync(semi, bucket.id)
    if (modifs) {
      semis.splice(semis.findIndex(s => s.id == semi.id), 1)
      getData()
    }
  }
}

function getSemiÉchantillonDate(semi: MDBSemi | undefined) {
  if (semi == undefined || semi.échantillonDate == undefined) {
    return '?'
  }

  const date = new Date(semi.échantillonDate)
  if (date.getFullYear() <= 2000)
    return 'N/D'

  return to_dd_MM(date)
}

const dialogÉchantillon = useTemplateRef('dialogÉchantillon')
async function openÉchantillon(semi: MDBSemi | undefined) {
  if (semi == undefined) {
    notifyError('Erreur: le semi n\'existe pas.')
    return
  }
  const result = await dialogÉchantillon.value?.openSemi(semi)
  if (result) {
    // Remove semi with this id
    semis.splice(semis.findIndex(s => s.id == semi.id), 1)
    getData()
  }
}

function changeSelectionSum() {
  selectedSum.value = computeSelectionSum()
}

async function onDrag(day: Date, args: any) {
  if (args['added'] != undefined) {
    try
    {
      const bucket = args.added.element as MDBVegcoBucket
      bucket.date = day.toISOString()
      bucket.dateChangéeManuellement = true
      const siteCS = storeU.siteSélectionné.lettresDappel == 'VGCO' ? 'VGCO' :
        storeU.getSiteLettresDappel(bucket.siteId)
      await apiProphet.put(`vgco/sites/${siteCS}/buckets/${bucket.id}`, bucket)
      Notify.success('Modification de la date du bucket avec succès');
    }
    catch (e) {
      notifyError(e)
    }
    finally {
      getData()
    }
  }
  if (args['removed'] != undefined) {
    // nothing
  }
  if (args['moved'] != undefined) {
    getData()
  }
}

function print() {
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.open()
    printWindow.document.write(generateReportHTML())
    printWindow.document.close()
    //setTimeout(() => printWindow.print(), 500)
  }
}

function generateReportHTML() {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Rapport de la semaine - ${selectedSite.value}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; border-left: 1px solid #000; border-right: 1px solid #000; padding: 2px; text-align: center; }
        .culture-heading { background-color: #f2f2f2; font-size: 14px; text-align: left; }
        .gap-between-weeks { border-left: 3px solid #000; border-right: 3px solid #000; }
        .total-row { font-weight: bold; }
      </style>
    </head>
    <body>
      <h3>Rapport de la semaine - Site: ${selectedSite.value} - Généré le: ${to_wd_dd_MMMM_hh_mm(new Date())}</h3>
      <table>
        <thead>
          <tr>
            <th></th>
            ${days.value.map(day => day
              ? `<th>${to_wd(day)}<br/>${to_dd_MM(day)}</th>`
              : '<th class="gap-between-weeks"></th>'
            ).join('')}
          </tr>
        </thead>

        ${cultures.value.map(culture => `
          <tbody>
            <tr>
              <th colspan="${days.value.length + 1}" class="culture-heading">${culture}</th>
            </tr>
            ${tableRows.map(row =>
              row.label === 'Modifier' ? '' :
              `<tr class="${row.field === 'totalQté' ? 'total-row' : ''}">
                <th>${row.label}</th>
                ${days.value.map(day => {
                  if (day === undefined) {
                    return '<td class="gap-between-weeks"></td>';
                  }
                  if (row.field === 'totalQté') {
                    return `<td style=''>${getTotalForDay(culture, day) || ''}</td>`;
                  }

                  const bucketsForDay = getBuckets(culture, day);
                  const cellContent = bucketsForDay.map(bucket => {
                    if (row.field == 'échantillon') {
                      const semi = getBucketSemi(bucket);
                      return `<td style='border: none; padding-left: 16px; padding-right: 16px; min-width: 40px'>${getSemiÉchantillonDate(semi) + '<br/>' + semi?.échantillonStatut}</td>`;
                    }

                    return `<td style='border: none; padding-left: 16px; padding-right: 16px; min-width: 40px'>${((bucket as any)[row.field] !== undefined ? (bucket as any)[row.field] : '?')}</td>` })
                  .join('');

                  return `<td><table><tr>${cellContent}</tr></table></td>`;
                }).join('')}
              </tr>`).join('')}
          </tbody>
        `).join('')}
      </table>
    </body>
    </html>
  `;
}

const intervalId = setInterval(async () => {
  if (await storeVegco.isRevisionChanged())
    newData.value = true
}, 3000)

onMounted(() => {
  getData()
  document.addEventListener('selectionchange', changeSelectionSum)
})

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
  document.removeEventListener('selectionchange', changeSelectionSum)
})
</script>

<template>
  <div v-if="selectedSum !== null" style="position: fixed; bottom: 36px; right: 32px; background: #222; color: #fff; padding: 12px 24px; border-radius: 8px; z-index: 9999;">
    Somme de la sélection : {{ selectedSum }}
  </div>
  <v-card class="mx-4 my-4 page-card" rounded="xl" style="height: 85vh; display: flex; flex-direction: column;" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-calendar-week" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Rapport de la semaine</div>
            <div class="text-caption text-medium-emphasis">Résumé de la semaine par culture</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn v-if="newData" color="primary" variant="text" prepend-icon="mdi-refresh" @click="getData()">Recharger les données</v-btn>
          <v-btn color="primary" variant="tonal" rounded="pill" icon="mdi-printer" @click="print()"/>
        </div>
      </div>

      <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
        <WeekSelector ref="weekSelector" v-model="selectedWeek" v-model:number-of-days="numberOfDays"/>
        <v-spacer/>
        <v-text-field
          v-model="numberOfWeeks" label="Nombre de semaines"
          type="number" min="1" max="4"
          density="compact" variant="outlined" rounded="lg" hide-details
          style="max-width: 200px;"
          @change="onWeekNumberChanged"/>
        <v-select
          v-model="selectedSite"
          :items="sites"
          label="Site"
          density="compact" variant="outlined" rounded="lg" hide-details
          style="max-width: 300px;"
          @update:model-value="getData()"/>
      </div>
    </div>
    <v-divider/>

    <div style="flex: 1 1 auto; overflow-x: auto; overflow-y: auto;" class="bigger-scrollbar">
      <table class="big-table">
        <thead>
          <tr>
            <th :style="`min-width:${$vuetify.display.xs ? 80 : 250}px`"/>
            <th
              v-for="(day, idx) in days" :key="idx"
              :style="`min-width: ${getColumnSize(idx)}`">
              {{ day != undefined ? to_wd(day) : '' }}
            </th>
          </tr>
          <tr>
            <th/>
            <th
              v-for="(day, idx) in days" :key="idx"
              :style="`background-color: ${getColumnColor(idx)};`">
              {{ day != undefined ? to_dd_MM(day) : '' }}
            </th>
          </tr>
        </thead>
        <tbody v-for="(culture, idcx) in cultures" :key="idcx">
          <tr>
            <th :class="`${$vuetify.display.xs ? 'text-subtitle1' : 'text-h5'} pt-3 culture-heading`">{{ culture }}</th>
            <th
              v-for="(day, idx) in days" :key="idx"
              style="overflow-x: scroll; white-space: nowrap;"
              :class="getCellClass(undefined, day)"/>
          </tr>
          <tr v-for="(row, rowIdx) in tableRows" :key="rowIdx">
            <th :class="row.thClass || 'small-borders'">
              {{ row.label }}
            </th>
            <th
              v-for="(day, idx) in days" :key="idx"
              style="overflow-x: scroll; white-space: nowrap;"
              :class="getCellClass(row, day)">
              <table
                v-if="row.field !== 'totalQté'"
                style="width: 100%; border-collapse: collapse;">
                <tbody>
                  <tr v-if="row.label === 'Modifier' && day != undefined && !$vuetify.display.mobile">
                    <draggable
                      class="draggable-region" group="buckets"
                      :list="getBuckets(culture, day)" item-key="id"
                      @change="onDrag(day, $event)">
                      <template #item="{ element: bucket }">
                        <td :style="`padding-left: 12px; padding-right: 12px; min-width: ${getColumnSize(idx)}`">
                          <v-card>
                            <v-btn
                              size="small" :color="getSemiVérifiéColor(getBucketSemi(bucket))"
                              class="ma-1" @click="openBuckets(bucket, day)">
                              {{ bucket.numéroSemi }}
                            </v-btn>
                            <v-icon
                              v-if="bucket?.semiFermé" size="x-large" color="red"
                              icon="mdi-close" style="opacity: 0.5"/>
                          </v-card>
                        </td>
                      </template>
                    </draggable>
                  </tr>
                  <tr v-else-if="row.label === 'Modifier' && day != undefined">
                    <td
                      v-for="bucket in getBuckets(culture, day)" :key="bucket.id"
                      :style="`padding-left: 12px; padding-right: 12px; min-width: ${getColumnSize(idx)}`">
                      <v-btn
                        size="small" :color="getSemiVérifiéColor(getBucketSemi(bucket))"
                        class="ma-1" @click="openBuckets(bucket, day)">
                        {{ bucket.numéroSemi }}
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-else-if="row.label === 'Échantillon' && day != undefined">
                    <td
                      v-for="bucket in getBuckets(culture, day)" :key="bucket.id"
                      :style="`padding-left: 12px; padding-right: 12px; min-width: ${getColumnSize(idx)}`">
                      <v-btn
                        size="small" :color="getSemiÉchantillonColor(getBucketSemi(bucket))"
                        class="ma-1" @click="openÉchantillon(getBucketSemi(bucket))">
                        {{ getSemiÉchantillonDate(getBucketSemi(bucket)) }}
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-else-if="row.field === 'quantitéCalc' && day != undefined">
                    <td
                      v-for="bucket in getBuckets(culture, day)" :key="bucket.id"
                      :style="`padding-left: 12px; padding-right: 12px; min-width: ${getColumnSize(idx)}`">
                      {{ (bucket as any)[row.field] == undefined ? '?' : (bucket as any)[row.field] }}
                      <v-tooltip v-if="getBucketCommandesTooltip(bucket)" activator="parent" location="top">
                        <pre>{{ getBucketCommandesTooltip(bucket) }}</pre>
                      </v-tooltip>
                    </td>
                  </tr>
                  <tr v-else>
                    <td
                      v-for="bucket in getBuckets(culture, day)" :key="bucket.id"
                      :style="`padding-left: 12px; padding-right: 12px; min-width: ${getColumnSize(idx)}`">
                      {{ (bucket as any)[row.field] == undefined ? '?' : (bucket as any)[row.field] }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <span v-else>{{ getTotalForDay(culture, day) }}</span>
            </th>
          </tr>
        </tbody>
      </table>
    </div>
  </v-card>

  <DialogBucket ref="dialogBucket"/>
  <DialogBuckets ref="dialogBuckets"/>
  <DialogSemiEchantillon ref="dialogÉchantillon"/>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

  .draggable-region {
    min-height: 50px;
    border: 1px dashed v-bind('theme.global.name.value === "dark" ? "#444" : "#DDD"');
    padding: 4px;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
  }

  .culture-heading {
    height: 75px;
    padding-left: 12px;
    padding-right: 12px;
    border-right: 1px solid;
  }

  .gap-between-weeks {
    border-left: 3px solid;
    border-right: 3px solid;
  }

  .small-borders {
    padding-left: 12px;
    padding-right: 12px;
    font-weight: normal;
    border-right: 1px solid;
    border-top: 0px;
    border-bottom: 0px;
  }

  th.gap-between-weeks.small-borders-bottom {
    border-right: 3px solid;
  }

  .small-borders-bottom {
    border-right: 1px solid;
    border-top: 1px solid;
    border-bottom: 5px solid;
    padding-left: 12px;
    padding-right: 12px;
    font-weight: bold;
  }

  /* For Firefox */
  div[style*="overflow-x: auto"] {
    scrollbar-width: thick;
  }
  /* For Chrome, Edge, Safari */
  div[style*="overflow-x: auto"]::-webkit-scrollbar {
    height: 12px;
  }
  div[style*="overflow-x: auto"]::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 8px;
  }

  .big-table {
    border-collapse: collapse;
    table-layout: fixed; /* Ensures that the table respects the width of its columns */
  }

  /* Alternate row background color */
  tr:nth-of-type(even) {
    background-color: v-bind('theme.global.name.value === "dark" ? "#333" : "#f5f5f5"');
  }

  /* Fix the first column */
  .big-table > td:first-child, th:first-child {
    position: sticky;
    left: 0;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"'); /* Set background based on theme */
    z-index: 4; /* Ensure it stays above other content */
  }

  /* Make the first row sticky */
  .big-table thead tr:first-child th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  /* Make the second row sticky, just below the first */
  .big-table thead tr:nth-child(2) th {
    position: sticky;
    top: 24px;
    z-index: 2;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  /* Make the first cell sticky */
  .big-table thead tr:first-child th:first-child{
    position: sticky;
    left: 0px;
    top: 0px;
    z-index: 3;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  /* Make the second cell sticky, just below the first */
  .big-table thead tr:nth-child(2) th:first-child {
    position: sticky;
    left: 0px;
    top: 24px;
    z-index: 3;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  .bigger-scrollbar {
    scrollbar-width: auto;
  }

</style>
